import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { SignInService } from '../services/sign-in/sign-in.service';
import { LottoUser } from '../../components/views/sign-in/user.model';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private signInService: SignInService,
    private toastrService: ToastrService
  ) {}

  public static doGetErrorMessage(err: HttpErrorResponse, responseType: any) {
    if (!(err && err.error)) {
      return `${err.status}: ${err.message}`;
    }

    let error = err.error;
    if (responseType === 'text') {
      error = JSON.parse(err.error);
    }

    if (err.error.message) {
      return `${err.error.message}`;
    }

    if (!error.errors) {
      return `${err.status}: ${err.message}`;
    }

    const { field } = error.errors[0];
    let { message } = error.errors[0];
    if (error.errors[0].parameters) {
      error.errors[0].parameters.forEach((data, index) => {
        message = message.replace(`{${index}}`, data);
      });
    }

    if (message.includes('LMSE21')) {
      const splitted = message.split('Field');
      if (splitted.length === 2) {
        message = `Field${splitted[1]}`;
      }
    }

    return `${err.status}: ${field ? `${field}: ` : ''} ${message}`;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(this.appendTokenHeader(req, this.signInService.getCurrentUser())).pipe(
      tap({
        next: (event: HttpEvent<any>) => event,
        error: (err: any) => this.checkForError(err, req),
      })
    );
  }

  private appendTokenHeader(req: HttpRequest<any>, user: LottoUser): HttpRequest<any> {
    let headers = req.headers;
    const authHeaders = user?.getAuthHeaders();

    if (authHeaders && Object.keys(authHeaders).length) {
      Object.entries(authHeaders).forEach(([key, value]: [key: string, value: string]) => {
        headers = headers.append(key, value);
      });
    }

    return req.clone({
      headers,
    });
  }

  private checkForError(err: any, req: HttpRequest<any>): void {
    if (err instanceof HttpErrorResponse) {
      if (this.checkForIgnoreNavigationConditions(err, req)) {
        return;
      }
      if (err.status != 404) {
        const messageKey = this.getErrorMessageKey(err);
        if (messageKey === 'gamstop.check.blocked.status.change') {
          this.toastrService.error(
            'Unable to change account status. An active GAMSTOP restriction was detected or could not be verified. The account must remain restricted.',
            'Status Change Blocked',
            { timeOut: 10000 }
          );
        } else {
          this.toastrService.error(this.getMessage(err, req), null, { timeOut: 5000 });
        }
      }
      if (err.status === 401 || err.status === 403) {
        if (!this.router.url.includes('sso')) {
          this.router.navigate(['/sso'], { queryParams: { redirect: this.router.url } });
        }
      }
    }
  }

  private getErrorMessageKey(err: any): string {
    const errors = err?.error?.errors;
    return errors && errors.length > 0 ? errors[0]['message-key'] : null;
  }

  private checkForIgnoreNavigationConditions(err: any, req: HttpRequest<any>): boolean {
    return req.url.includes('documents-requests') && (err.status == 403 || err.status);
  }

  private getMessage(err: HttpErrorResponse, req: HttpRequest<any>): string {
    if (req.method === 'OPTIONS') {
      return;
    }

    return AuthInterceptor.doGetErrorMessage(err, req.responseType);
  }
}
